import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Grid,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../services/api';
import UserManagementDialog from './UserManagementDialog';
import { useOrgCuratedCurrencies } from '../hooks/useOrgCuratedCurrencies';
import { formatCurrencyPickerLabel } from '../utils/iso4217';
import { formatDecimalFixed } from '../utils/timesheetEntryDisplay';
import AccountBudgetCard from './AccountBudgetCard';
import { AccountDashboardProvider, useAccountDashboard } from './accountDashboard/AccountDashboardContext';
import AccountDashboardSection from './accountDashboard/AccountDashboardSection';
import AccountDashboardControls from './accountDashboard/AccountDashboardControls';
import AccountTasksTable from './accountDashboard/AccountTasksTable';
import AccountUsersTable from './accountDashboard/AccountUsersTable';
import AccountTimesheetSection from './accountDashboard/AccountTimesheetSection';
import type { AccountBudgetProgressDto } from '../types/accountBudget';

interface Account {
  id: string;
  name: string;
  companyId: string;
  organizationId: string;
  description?: string;
  accountNumber?: string;
  billingRate?: number;
  currency: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AccountFormData {
  name: string;
  description: string;
  accountNumber: string;
  billingRate: string;
  currency: string;
  isActive: boolean;
}

interface Task {
  id: string;
  name: string;
  accountId: string;
  organizationId: string;
  taskNumber?: string;
  description?: string;
  isBillable: boolean;
  billingRate?: number;
  currency?: string;
  isActive: boolean;
}

interface AccountViewProps {
  accountId: string;
  accountName: string;
  companyId: string;
  organizationId: string;
  onBack: () => void;
  onOpenTask: (taskId: string, taskName: string) => void;
  userRole?: string;
}

export default function AccountView(props: AccountViewProps) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setAccount(null);
    setLoading(true);
    api.getAccount(props.accountId).then((response) => {
      if (response.success && response.data) {
        setAccount(response.data);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [props.accountId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading account...</Typography>
      </Box>
    );
  }

  if (!account) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Account not found</Alert>
        <Button onClick={props.onBack} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  return (
    <AccountDashboardProvider accountId={props.accountId} accountCurrency={account.currency}>
      <AccountViewBody {...props} initialAccount={account} />
    </AccountDashboardProvider>
  );
}

function AccountViewBody({
  accountId,
  companyId,
  organizationId,
  onBack,
  onOpenTask,
  userRole = 'MEMBER',
  initialAccount
}: AccountViewProps & { initialAccount: Account }) {
  const { accessGranted, refreshBudgetConfig } = useAccountDashboard();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<Account | null>(initialAccount);
  const [budgetProgress, setBudgetProgress] = useState<AccountBudgetProgressDto | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [formData, setFormData] = useState<AccountFormData>({
    name: '',
    description: '',
    accountNumber: '',
    billingRate: '',
    currency: 'EUR',
    isActive: true
  });
  const [taskFormData, setTaskFormData] = useState({
    name: '',
    taskNumber: '',
    description: '',
    isBillable: true,
    billingRate: '',
    currency: 'EUR',
    isActive: true
  });

  const { curatedCodes } = useOrgCuratedCurrencies(organizationId);
  const canEdit = userRole === 'OWNER' || userRole === 'MANAGER';
  const economyVisible = accessGranted === true;

  useEffect(() => {
    setAccount(initialAccount);
    setFormData({
      name: initialAccount.name,
      description: initialAccount.description || '',
      accountNumber: initialAccount.accountNumber || '',
      billingRate: initialAccount.billingRate?.toString() || '',
      currency: initialAccount.currency || 'EUR',
      isActive: initialAccount.isActive
    });
    loadTasks();
  }, [accountId, initialAccount]);

  const loadBudgetProgress = useCallback(async () => {
    if (!economyVisible) {
      setBudgetProgress(null);
      return;
    }
    try {
      const res = await api.getAccountBudgetProgress(accountId);
      if (res.success && res.data.hasBudget) {
        setBudgetProgress(res.data.progress);
      } else {
        setBudgetProgress(null);
      }
    } catch {
      setBudgetProgress(null);
    }
  }, [accountId, economyVisible]);

  useEffect(() => {
    loadBudgetProgress();
  }, [loadBudgetProgress]);

  const handleBudgetUpdated = useCallback(async () => {
    await loadBudgetProgress();
    await refreshBudgetConfig();
  }, [loadBudgetProgress, refreshBudgetConfig]);

  const loadAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAccount(accountId);
      if (response.success && response.data) {
        const accountData = response.data;
        setAccount(accountData);
        setFormData({
          name: accountData.name,
          description: accountData.description || '',
          accountNumber: accountData.accountNumber || '',
          billingRate: accountData.billingRate?.toString() || '',
          currency: accountData.currency || 'EUR',
          isActive: accountData.isActive
        });
      } else {
        setError('Account not found');
      }
    } catch (err: any) {
      console.error('Error loading account:', err);
      setError(err.response?.data?.message || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async () => {
    try {
      setTasksLoading(true);
      const response = await api.getTasks({ accountId });
      setTasks(response.data || []);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      const updateData = {
        organizationId,
        companyId,
        ...formData,
        billingRate: formData.billingRate ? parseFloat(formData.billingRate) : undefined
      };
      const response = await api.updateAccount(accountId, updateData);
      if (response.success) {
        await loadAccount();
        setEditDialogOpen(false);
      } else {
        setError(response.message || 'Failed to update account');
      }
    } catch (err: any) {
      console.error('Error updating account:', err);
      setError(err.response?.data?.message || 'Failed to update account');
    }
  };

  const handleOpenTaskDialog = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setTaskFormData({
        name: task.name,
        taskNumber: task.taskNumber || '',
        description: task.description || '',
        isBillable: task.isBillable,
        billingRate: task.billingRate?.toString() || '',
        currency: task.currency || account?.currency || 'EUR',
        isActive: task.isActive
      });
    } else {
      setEditingTask(null);
      setTaskFormData({
        name: '',
        taskNumber: '',
        description: '',
        isBillable: true,
        billingRate: '',
        currency: account?.currency || 'EUR',
        isActive: true
      });
    }
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async () => {
    try {
      setError(null);
      const taskData = {
        ...taskFormData,
        accountId,
        organizationId,
        billingRate: taskFormData.billingRate ? parseFloat(taskFormData.billingRate) : undefined,
        currency: taskFormData.currency
      };
      
      if (editingTask) {
        await api.updateTask(editingTask.id, taskData);
      } else {
        await api.createTask(taskData);
      }
      await loadTasks();
      setTaskDialogOpen(false);
    } catch (err: any) {
      console.error('Error saving task:', err);
      setError(err.response?.data?.message || 'Failed to save task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await api.deleteTask(taskId);
        await loadTasks();
      } catch (err: any) {
        console.error('Error deleting task:', err);
        setError(err.response?.data?.message || 'Failed to delete task');
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete this account? This cannot be undone.')) return;
    try {
      setError(null);
      await api.deleteAccount(accountId);
      onBack();
    } catch (err: any) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  if (loading && !account) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading account...</Typography>
      </Box>
    );
  }

  if (!account) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{account.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteAccount}
              >
                Delete
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
        </Box>
      </Box>

      <AccountDashboardControls />

      <AccountDashboardSection />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <AccountTasksTable
                  tasks={tasks}
                  tasksLoading={tasksLoading}
                  canEdit={canEdit}
                  economyVisible={economyVisible}
                  budgetProgress={budgetProgress}
                  onOpenTask={onOpenTask}
                  onEditTask={(task) => handleOpenTaskDialog(task as Task)}
                  onDeleteTask={handleDeleteTask}
                  onAddTask={() => handleOpenTaskDialog()}
                  onTargetsSaved={handleBudgetUpdated}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <AccountUsersTable
                  accountId={accountId}
                  economyVisible={economyVisible}
                  canEdit={canEdit}
                  onManageUsers={() => setUsersDialogOpen(true)}
                  usersRefreshKey={usersRefreshKey}
                />
              </CardContent>
            </Card>

            <AccountTimesheetSection accountId={accountId} organizationId={organizationId} />
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Account Details */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Account Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{account.name}</Typography>
                  </Box>
                  {account.description && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Description</Typography>
                      <Typography variant="body1">{account.description}</Typography>
                    </Box>
                  )}
                  {account.accountNumber && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Account Number</Typography>
                      <Typography variant="body1">{account.accountNumber}</Typography>
                    </Box>
                  )}
                  {formatDecimalFixed(account.billingRate, 2) != null && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Billing Rate</Typography>
                      <Typography variant="body1">
                        {account.currency} {formatDecimalFixed(account.billingRate, 2)}/hour
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={account.isActive ? 'Active' : 'Inactive'}
                      color={account.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <AccountBudgetCard
              accountId={accountId}
              accountCurrency={account.currency}
              organizationId={organizationId}
              canEdit={canEdit}
              onBudgetUpdated={handleBudgetUpdated}
            />

          </Stack>
        </Grid>
      </Grid>

      {/* Edit Account Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Account Number"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
              fullWidth
            />
            <TextField
              label="Billing Rate"
              value={formData.billingRate}
              onChange={(e) => setFormData({ ...formData, billingRate: e.target.value })}
              fullWidth
              type="number"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{formData.currency}</Typography>
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                label="Currency"
                disabled={curatedCodes.length === 0}
              >
                {curatedCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {formatCurrencyPickerLabel(code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Task Name"
              value={taskFormData.name}
              onChange={(e) => setTaskFormData({ ...taskFormData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Task Number"
              value={taskFormData.taskNumber}
              onChange={(e) => setTaskFormData({ ...taskFormData, taskNumber: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={taskFormData.description}
              onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={taskFormData.currency}
                label="Currency"
                onChange={(e) => setTaskFormData({ ...taskFormData, currency: e.target.value })}
                disabled={curatedCodes.length === 0}
              >
                {curatedCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {formatCurrencyPickerLabel(code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Billing Rate"
              value={taskFormData.billingRate}
              onChange={(e) => setTaskFormData({ ...taskFormData, billingRate: e.target.value })}
              fullWidth
              type="number"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{taskFormData.currency}</Typography>
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={taskFormData.isBillable}
                  onChange={(e) => setTaskFormData({ ...taskFormData, isBillable: e.target.checked })}
                />
              }
              label="Billable"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={taskFormData.isActive}
                  onChange={(e) => setTaskFormData({ ...taskFormData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveTask} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users Management Dialog */}
      {canEdit && (
        <UserManagementDialog
          open={usersDialogOpen}
          onClose={() => {
            setUsersDialogOpen(false);
            setUsersRefreshKey((k) => k + 1);
          }}
          title={`Manage Users - ${account.name}`}
          entityId={accountId}
          entityType="account"
          organizationId={organizationId}
        />
      )}
    </Box>
  );
}
